import { useState, useEffect, useCallback } from 'react';

interface UseActiveSectionOptions {
  headerHeight?: number;
  threshold?: number;
}

export function useActiveSection(sectionIds: string[], options: UseActiveSectionOptions = {}) {
  const { headerHeight = 140 } = options;
  const [activeSection, setActiveSection] = useState<string>('');

  const updateActiveSection = useCallback(() => {
    if (sectionIds.length === 0) return;

    const scrollPosition = window.scrollY + headerHeight + 50; // Add some buffer
    let currentSection = sectionIds[0]; // Default to first section

    // Find the section that the user is currently viewing
    for (const sectionId of sectionIds) {
      const element = document.getElementById(sectionId);
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const elementTop = window.scrollY + rect.top;
      const elementBottom = elementTop + rect.height;

      // Check if the scroll position is within this section
      if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
        currentSection = sectionId;
        break;
      }

      // If we've scrolled past this section, it might be the active one
      if (scrollPosition >= elementTop) {
        currentSection = sectionId;
      }
    }

    setActiveSection(currentSection);
  }, [sectionIds, headerHeight]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      const elementTop = element.offsetTop - headerHeight - 20;

      // Immediately update active section for responsive UI
      setActiveSection(sectionId);

      window.scrollTo({
        top: Math.max(0, elementTop),
        behavior: 'smooth',
      });
    },
    [headerHeight]
  );

  useEffect(() => {
    // Set initial active section
    updateActiveSection();

    // Add scroll listener with throttling
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Also update on resize
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [updateActiveSection]);

  return {
    activeSection,
    scrollToSection,
    setActiveSection,
  };
}
